/* eslint-disable max-lines */
import React from 'react';

import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import numOrDefault from 'lib/helpers/numOrDefault';

import isEstimated from 'components/practice/BeyondBilling/lib/isEstimated';
import { calculateInvoiceTaxFromItems } from 'components/practice/NewAppointment/components/Sections/Pricing/components/ProceduresProduct/components/SelectedItems/helpers';
import { calculateProceduresTotal } from 'components/public/lib/utils';
import Allowed from 'components/shared/Permissions/Allowed';

import { procedureTypes } from '../../../lib/constants/constants';
import { Capitalize, Truncate, ia, mString } from '../../../lib/helpers/utility';
import { currentPractice } from '../../practice/practiceState';

const NoteCell = ({ note }) => (
  <td className="text-left text-xxs">
    <Tippy
      className="tippy-dark"
      disabled={!note}
      placement={note?.length > 400 ? 'left' : 'top'}
      content={note}>
      <div>{Truncate(note, 30)}</div>
    </Tippy>
  </td>
);

const InvoiceReadOnly = ({ invoices, ref, from, fromPatientPortal = false }) => {
  const practice = useRecoilValue(currentPractice);

  const claims = practice?.display_settings?.claims;

  return (
    <div className="flex flex-col !gap-2" ref={ref}>
      {ia(invoices) &&
        invoices?.map((i) => {
          const tax = numOrDefault(i?.tax);
          const writeOff = numOrDefault(i?.write_off);
          const amountPaid = numOrDefault(i?.amount_paid);
          const discountAmount = i?.discount;
          const currentBalance = i?.total_amount - amountPaid - discountAmount - writeOff + tax;

          const hasERAPaid = !isEstimated({ procedures: i?.procedures });
          const insPaid = calculateProceduresTotal(i?.procedures)?.ins_payment;

          return (
            <>
              <div key={i} className="!p-4 ">
                <div className="flex gap-3"></div>

                <div className="grid !gap-4">
                  <table className="bg-inherit">
                    <thead className="text-xxs uppercase text-neutral-600">
                      <tr className="border-x-0 !border-b border-t-0 border-solid border-b-neutral-100">
                        <td className="!p-1 text-left !font-500" title="Details">
                          Details
                        </td>
                        <td className="!p-1 text-left !font-500" title="Details">
                          DOS
                        </td>
                        <td className="!p-1 !font-500" title="Type">
                          Type
                        </td>
                        <td className="!p-1 text-center !font-500" title="Quantity">
                          Qty
                        </td>
                        <Allowed requiredPermissions={!fromPatientPortal && 'payments.read'}>
                          <td className="!p-1 !font-500" title="Charge">
                            Charge
                          </td>

                          <td className="!p-1 !font-500" title="Charge">
                            Discount
                          </td>
                          <td className="!p-1 !font-500" title="Charge">
                            Tax
                          </td>

                          {claims ? (
                            <>
                              <td
                                className="w-[105px] !p-1 !font-500"
                                title="Estimated Insurance Payment">
                                Est. Ins. Pay
                              </td>
                              <td
                                className="w-[105px] !p-1 !font-500"
                                title="Estimated Insurance Adjustment">
                                Est. Ins. Adj
                              </td>
                            </>
                          ) : null}
                          <td className="w-[105px] !p-1 !font-600" title="Patient Balance">
                            Pt. bal
                          </td>

                          <td className="!font-600" title="Note">
                            Note
                          </td>
                        </Allowed>
                      </tr>
                    </thead>

                    <tbody>
                      {/* Services */}
                      {ia(i?.services || i?.procedures) &&
                        (i?.services || i?.procedures || []).map((procedure, index) => {
                          const {
                            name,
                            type,
                            amount,
                            total_cost_cents,
                            pt_balance,
                            discount,
                            note,
                            from_date
                          } = procedure || {};

                          const {
                            charge = 0,
                            ins_adjustment = 0,
                            ins_payment = 0,
                            qty
                          } = calculateProceduresTotal([procedure]);

                          const taxAmount = calculateInvoiceTaxFromItems({
                            procedures: [procedure]
                          });

                          return (
                            <tr
                              key={name + index}
                              className="border-x-0 !border-b  border-t-0 border-solid border-b-neutral-100 font-500 leading-5 text-neutral-800 hover:bg-transparent">
                              <td className="max-w-[240px] !py-2 !pl-0 text-left text-xs">
                                {name}
                              </td>
                              <td className="max-w-[240px] !py-2 !pl-0 text-left text-xs">
                                {from_date || ''}
                              </td>
                              <td className="text-xxs">
                                {procedureTypes?.find((o) => o?.value === type)?.label ||
                                  Capitalize((type || '').replace('_', ' ')) ||
                                  'Service'}
                              </td>
                              <td className="!py-2 text-center text-xxs">{qty || 1}</td>
                              <Allowed requiredPermissions={!fromPatientPortal && 'payments.read'}>
                                <td className="!py-2 text-xxs">
                                  {mString(charge ?? amount ?? total_cost_cents ?? 0)}
                                </td>
                                <td className="!py-2 text-xxs">
                                  {mString(discount?.amount_cents)}
                                </td>
                                <td className="!py-2 text-xxs">{mString(taxAmount)}</td>

                                {claims ? (
                                  <>
                                    <td className="!py-2 text-xxs">{mString(ins_payment || 0)}</td>
                                    <td className="!py-2 text-xxs">
                                      {mString(ins_adjustment || 0)}
                                    </td>
                                  </>
                                ) : null}
                                <td className="!py-2 text-xs font-600">
                                  {mString((pt_balance ?? total_cost_cents ?? 0) * qty)}
                                </td>
                              </Allowed>

                              <NoteCell note={note} />
                            </tr>
                          );
                        })}

                      {/* Packages with procedures and products*/}
                      {ia(i?.packages) &&
                        i?.packages.map(
                          (
                            {
                              name,
                              total_amount_cents,
                              procedures,
                              products,
                              status,
                              sales_count,
                              note
                            },
                            index
                          ) => {
                            const hasProcedures = ia(procedures);
                            const hasProducts = ia(products);

                            return (
                              <>
                                <tr
                                  key={name + index}
                                  className={cs(
                                    'font-500 text-neutral-800  hover:bg-transparent',
                                    status === 'cancelled' && 'line-through',
                                    !hasProcedures &&
                                      'border-x-0 !border-b border-t-0 border-solid border-b-neutral-100'
                                  )}>
                                  <td className="max-w-[240px] !py-2 !pl-0 text-left text-xs">
                                    {name}
                                  </td>
                                  <td />
                                  <td className="!py-2 text-xxs">Package</td>
                                  <td className="!py-2 text-center text-xxs">{sales_count || 1}</td>
                                  <Allowed
                                    requiredPermissions={!fromPatientPortal && 'payments.read'}>
                                    <td className="!py-2 text-xxs">
                                      {mString(total_amount_cents)}
                                    </td>
                                    <td />
                                    <td />
                                    {claims ? (
                                      <>
                                        <td className="text-xxs">{mString(0)}</td>
                                        <td className="text-xxs">{mString(0)}</td>
                                      </>
                                    ) : null}
                                    <td className="!py-2 text-xs font-600">
                                      {mString(total_amount_cents)}
                                    </td>
                                  </Allowed>

                                  <NoteCell note={note} />
                                </tr>

                                {hasProcedures &&
                                  procedures.map((procedure, index) => {
                                    const { name, quantity, discount } = procedure;

                                    const taxAmount = calculateInvoiceTaxFromItems({
                                      procedures: [procedure]
                                    });

                                    return (
                                      <tr
                                        key={name + index}
                                        className={cs(
                                          'text-xxs text-neutral-800 hover:bg-transparent',
                                          status === 'cancelled' && 'line-through',
                                          procedures?.length - 1 === index &&
                                            !hasProducts &&
                                            'border-x-0 !border-b border-t-0 border-solid border-b-neutral-100'
                                        )}>
                                        <td className="max-w-[240px] !py-1 !pl-3 text-left">
                                          {name}
                                        </td>
                                        <td />
                                        <td />
                                        <td className="!py-1 text-center">{quantity || 1}</td>
                                        <td />
                                        <td>{mString(discount?.amount_cents)}</td>
                                        <td>{mString(taxAmount)}</td>
                                        {claims ? <td colSpan={2}></td> : null}
                                        <td />
                                      </tr>
                                    );
                                  })}

                                {hasProducts &&
                                  products.map((product, index) => {
                                    const { name, quantity, discount } = product;

                                    const taxAmount = calculateInvoiceTaxFromItems({
                                      products: [product]
                                    });

                                    return (
                                      <tr
                                        key={name + index}
                                        className={cs(
                                          'text-xxs text-neutral-800 hover:bg-transparent',
                                          status === 'cancelled' && 'line-through',
                                          products?.length - 1 === index &&
                                            'border-x-0 !border-b border-t-0 border-solid border-b-neutral-100'
                                        )}>
                                        <td className="max-w-[240px] !py-1 !pl-3 text-left">
                                          {name}
                                        </td>
                                        <td />
                                        <td />
                                        <td className="!py-1 text-center">{quantity || 1}</td>
                                        <td />
                                        <td>{mString(discount?.amount_cents)}</td>
                                        <td>{mString(taxAmount)}</td>
                                        {claims ? <td colSpan={2}></td> : null}
                                        <td />
                                      </tr>
                                    );
                                  })}
                              </>
                            );
                          }
                        )}

                      {/* Products */}
                      {ia(i?.products) &&
                        i?.products.map((product, index) => {
                          const { name, amount, sales_count, total_amount_cents, discount, note } =
                            product;

                          const taxAmount = calculateInvoiceTaxFromItems({
                            products: [product]
                          });

                          return (
                            <tr
                              key={name + index}
                              className="border-x-0 !border-b border-t-0 border-solid border-b-neutral-100 font-500 text-neutral-800 hover:bg-transparent">
                              <td className="max-w-[240px] !py-2 !pl-0 text-left text-xs text-neutral-900">
                                {name}
                              </td>
                              <td />
                              <td className="!py-2 text-xxs">Product</td>
                              <td className="!py-2 text-center text-xxs">{sales_count || 1}</td>
                              <Allowed requiredPermissions={!fromPatientPortal && 'payments.read'}>
                                <td className="!py-2 text-xxs">
                                  {mString(amount ?? total_amount_cents)}
                                </td>
                                <td className="!py-2 text-xxs">
                                  {mString(discount?.amount_cents)}
                                </td>
                                <td className="!py-2 text-xxs">{mString(taxAmount)}</td>

                                {claims ? (
                                  <>
                                    <td className="!py-2 text-xxs">{mString(0)}</td>
                                    <td className="!py-2 text-xxs">{mString(0)}</td>
                                  </>
                                ) : null}
                                <td className="!py-2 text-xs font-600 ">
                                  {mString(total_amount_cents)}
                                </td>
                              </Allowed>

                              <NoteCell note={note} />
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <Allowed requiredPermissions={!fromPatientPortal && 'payments.read'}>
                  <div className="m-[16px_-17px_-17px_-17px] flex h-10 items-center !gap-2 !p-4 text-xs font-600 uppercase text-primary-900">
                    <div className="w-1/6">Self Pay Amt.</div>
                    {hasERAPaid && insPaid > 0 && <div className="w-1/6">INS. PAID</div>}
                    {amountPaid > 0 && <div className="w-1/6">PT. PAID</div>}
                    {(discountAmount > 0 || from === 'quote') && (
                      <div className="w-1/6">Discount</div>
                    )}
                    {(tax > 0 || from == 'quote') && <div className="w-1/6">Tax</div>}
                    {writeOff > 0 && <div className="w-1/6">Write-Off</div>}
                    <div className="flex  w-2/6 flex-auto flex-col items-end">
                      <label className="mb-0 text-sm font-600">Balance</label>
                    </div>
                  </div>
                </Allowed>

                <Allowed requiredPermissions={!fromPatientPortal && 'payments.read'}>
                  <div className="m-[16px_-16px_-16px_-16px] flex min-h-[40px] items-center !gap-2 bg-gradient-to-r from-[#004F6B3D] to-[#004f6b0d] !px-4 uppercase text-primary-900">
                    <div className="col-span-1 w-1/6 font-600">{mString(i?.total_amount)}</div>
                    {hasERAPaid && insPaid > 0 && (
                      <div className="col-span-1 w-1/6 font-600">{mString(insPaid)}</div>
                    )}
                    {amountPaid > 0 && (
                      <div className="col-span-1 w-1/6 font-600">{mString(amountPaid)}</div>
                    )}

                    {(discountAmount > 0 || from === 'quote') && (
                      <div className="col-span-1 w-1/6 font-600">{mString(discountAmount)}</div>
                    )}

                    {(tax > 0 || from == 'quote') && (
                      <div className="col-span-1 w-1/6 font-600">{mString(tax)}</div>
                    )}

                    {writeOff > 0 && from !== 'quote' && (
                      <div className="col-span-1 w-1/6 pl-2 font-600">{mString(writeOff)}</div>
                    )}

                    <div className="flex w-2/6 flex-auto flex-col items-end">
                      <label className="!text-sm font-600">{mString(currentBalance)}</label>
                    </div>
                  </div>
                </Allowed>
              </div>
            </>
          );
        })}
    </div>
  );
};

export default InvoiceReadOnly;
